import { createTheme, alpha } from '@mui/material/styles';

// Design Tokens - A cohesive color palette inspired by Linear/Vercel/Stripe
const tokens = {
  // Primary gradient colors
  primary: {
    main: '#6366F1', // Indigo
    light: '#818CF8',
    dark: '#4F46E5',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
  },
  // Secondary/accent colors
  secondary: {
    main: '#EC4899', // Pink
    light: '#F472B6',
    dark: '#DB2777',
    gradient: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
  },
  // Success colors
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    gradient: 'linear-gradient(135deg, #10B981 0%, #06D6A0 100%)',
  },
  // Warning colors
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)',
  },
  // Error colors
  error: {
    main: '#EF4444',
    light: '#F87171',
    dark: '#DC2626',
  },
  // Info colors
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#2563EB',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
  },
  // Background colors
  background: {
    default: '#FAFBFC',
    paper: '#FFFFFF',
    subtle: '#F8FAFC',
    dark: '#0F172A',
    gradient: 'linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
  },
  // Text colors
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    disabled: '#94A3B8',
    inverse: '#FFFFFF',
  },
  // Border colors
  border: {
    light: '#E2E8F0',
    main: '#CBD5E1',
    focus: '#6366F1',
  },
};

// Glassmorphism effects
const glass = {
  background: 'rgba(255, 255, 255, 0.7)',
  backgroundDark: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

// Shadows - softer, more modern
const shadows = {
  xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
  sm: '0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
  glow: `0 0 40px ${alpha(tokens.primary.main, 0.15)}`,
  glowPrimary: `0 0 30px ${alpha(tokens.primary.main, 0.25)}`,
  glowSuccess: `0 0 30px ${alpha(tokens.success.main, 0.25)}`,
  card: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04)',
  cardHover: '0 10px 40px -10px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
};

// Animation easings
const easings = {
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// Create the Material-UI theme
// A gradient is a background-IMAGE. MUI's disabled rule sets only
// background-color, so a switched-off button keeps its gradient and goes on
// looking pressable. Clearing the image lets MUI's grey through, which is
// better than restating MUI's grey here and having two rules able to disagree.
// DYN-287.
const clearTheGradientWhenDisabled = {
  '&.Mui-disabled': {
    backgroundImage: 'none',
  },
};

// A hover rule written with this key steps aside for an item that is showing a
// keyboard focus marker, and keeps exactly the weight it would have had without
// it. MUI expresses that marker as a background under `.Mui-focusVisible`, and
// nothing else: ButtonBase sets `outline: 0`, so there is no ring behind it.
//
// Weight is the whole reason for the spelling. `:not(.Mui-focusVisible)` would
// also step aside, but it adds a class, and a hover rule one class heavier
// starts winning arguments it has no business in: on a menu item it would
// outrank MUI's own selected-and-hovered rule, so a selected row would lose its
// selection colour under the pointer. `:where()` contributes nothing and
// `:not()` contributes its argument, so this pair contributes nothing at all.
//
// The cost is that a browser too old for `:where()` drops the whole rule and
// shows no hover tint. Of the browsers this app builds for, caniuse lists one
// (QQ for Android) as less than full support, and losing a hover tint there is
// the right direction to fail in. DYN-288.
const hoverUnlessShowingFocus = '&:hover:not(:where(.Mui-focusVisible))';

// The two chip styles the scoped slots below hand back. Written up here so the
// slot callbacks stay short enough to read the condition in. DYN-62.
const chipBrandTint = {
  backgroundColor: alpha(tokens.primary.main, 0.08),
  color: tokens.primary.main,
  [hoverUnlessShowingFocus]: {
    backgroundColor: alpha(tokens.primary.main, 0.12),
  },
};

const chipNeutralBorder = {
  borderColor: tokens.border.light,
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.primary.main,
      light: tokens.primary.light,
      dark: tokens.primary.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.secondary.main,
      light: tokens.secondary.light,
      dark: tokens.secondary.dark,
      contrastText: '#FFFFFF',
    },
    success: {
      main: tokens.success.main,
      light: tokens.success.light,
      dark: tokens.success.dark,
    },
    warning: {
      main: tokens.warning.main,
      light: tokens.warning.light,
      dark: tokens.warning.dark,
    },
    error: {
      main: tokens.error.main,
      light: tokens.error.light,
      dark: tokens.error.dark,
    },
    info: {
      main: tokens.info.main,
      light: tokens.info.light,
      dark: tokens.info.dark,
    },
    background: {
      default: tokens.background.default,
      paper: tokens.background.paper,
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
      disabled: tokens.text.disabled,
    },
    divider: tokens.border.light,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: tokens.text.secondary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    shadows.xs,
    shadows.sm,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows.xl,
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: tokens.background.subtle,
          },
          '&::-webkit-scrollbar-thumb': {
            background: tokens.border.main,
            borderRadius: '4px',
            '&:hover': {
              background: tokens.text.secondary,
            },
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          transition: `all 0.2s ${easings.smooth}`,
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        // These are scoped to a colour on purpose. An unqualified slot such as
        // `contained` is emitted AFTER MUI's own `containedError`, so it repaints
        // the button in the brand and the colour prop is silently ignored.
        // A Button with no `color` resolves to primary, so scoping to Primary
        // leaves every such button exactly as it was. DYN-62.
        containedPrimary: {
          background: tokens.primary.gradient,
          '&:hover': {
            background: tokens.primary.gradient,
            filter: 'brightness(1.05)',
          },
          ...clearTheGradientWhenDisabled,
        },
        containedSecondary: {
          background: tokens.secondary.gradient,
          '&:hover': {
            background: tokens.secondary.gradient,
            filter: 'brightness(1.05)',
          },
          ...clearTheGradientWhenDisabled,
        },
        outlinedPrimary: {
          borderColor: tokens.border.light,
          '&:hover': {
            borderColor: tokens.primary.main,
            background: alpha(tokens.primary.main, 0.04),
          },
        },
        textPrimary: {
          '&:hover': {
            background: alpha(tokens.primary.main, 0.06),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: shadows.card,
          border: `1px solid ${tokens.border.light}`,
          backgroundImage: 'none',
          transition: `all 0.3s ${easings.smooth}`,
          '&:hover': {
            boxShadow: shadows.cardHover,
            borderColor: 'transparent',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: shadows.sm,
        },
        elevation2: {
          boxShadow: shadows.md,
        },
        elevation3: {
          boxShadow: shadows.lg,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: `all 0.2s ${easings.smooth}`,
            '& fieldset': {
              borderColor: tokens.border.light,
              transition: `all 0.2s ${easings.smooth}`,
            },
            // These two rules step aside for a field in error, which lets
            // MUI's own .Mui-error rule decide the outline. Without the guard
            // they outrank it, so an invalid box turned neutral under the
            // pointer and brand purple once you clicked in, which is exactly
            // when it is being read. Stepping aside rather than restating the
            // red here means MUI still decides the combinations this does not
            // name, notably a field that is invalid AND switched off, which
            // stays grey. Both step aside for a disabled field too: the hover
            // rule was overruling MUI's disabled outline in the same way, which
            // predates this change.
            //
            // THE TWO GUARDS MUST MATCH. They carry equal weight, so which one
            // wins on a field that is focused AND under the pointer is decided
            // by which is written last. Guarding only one of them silently
            // reorders that pair, and a focused box would turn neutral the
            // moment the pointer rested on it. DYN-287.
            '&:hover:not(.Mui-error):not(.Mui-disabled) fieldset': {
              borderColor: tokens.border.main,
            },
            '&.Mui-focused:not(.Mui-error):not(.Mui-disabled) fieldset': {
              borderColor: tokens.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        // Chips default to color="default", so both the default and the primary
        // chip keep the brand tint they have today and only a chip that asked
        // for another colour changes.
        //
        // A callback rather than a narrower selector. MUI 5.18 resolves
        // `filledDefault` at runtime but does not declare it in ChipClasses, so
        // that slot key does not typecheck, and the obvious alternative,
        // `&.MuiChip-colorDefault`, raises the rule from one class to two. That
        // extra specificity beats things it has no business beating: an `sx`
        // prop on the call site, and MUI's own `.Mui-focusVisible` background,
        // which would cost a clickable chip its focus ring (AuditLogs has one).
        // The callback picks the same chips and leaves specificity alone.
        filled: ({ ownerState }) =>
          ownerState.color === 'default' || ownerState.color === 'primary' ? chipBrandTint : {},
        outlined: ({ ownerState }) =>
          ownerState.color === 'default' || ownerState.color === 'primary' ? chipNeutralBorder : {},
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.background.dark,
          borderRadius: 8,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: shadows.xl,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.border.light}`,
          backgroundColor: glass.backgroundDark,
          backdropFilter: glass.backdropFilter,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: `all 0.2s ${easings.smooth}`,
          [hoverUnlessShowingFocus]: {
            backgroundColor: alpha(tokens.primary.main, 0.06),
          },
          // The selected pair is deliberately NOT guarded, and that is a
          // measurement rather than an oversight. MUI writes its own
          // `.Mui-selected:hover` after its `.Mui-selected.Mui-focusVisible`,
          // at the same weight, so a selected row under the pointer loses the
          // stronger focus tint to MUI itself whatever this file does. Guarding
          // here would change nothing on the screen and would read as if it
          // had. It still looks selected either way.
          '&.Mui-selected': {
            backgroundColor: alpha(tokens.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.12),
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: '0 4px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: tokens.background.subtle,
            color: tokens.text.secondary,
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: 'none',
            padding: '14px 16px',
            '&:first-of-type': {
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
            },
            '&:last-of-type': {
              borderTopRightRadius: 10,
              borderBottomRightRadius: 10,
            },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: `background-color 0.2s ${easings.smooth}`,
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.02),
            },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${tokens.border.light}`,
            padding: '16px',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: shadows.lg,
          transition: `all 0.3s ${easings.smooth}`,
          '&:hover': {
            boxShadow: shadows.xl,
            transform: 'scale(1.05)',
          },
        },
        primary: {
          background: tokens.primary.gradient,
          ...clearTheGradientWhenDisabled,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: shadows['2xl'],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: shadows.xl,
          border: `1px solid ${tokens.border.light}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          padding: '10px 12px',
          transition: `background-color 0.15s ${easings.smooth}`,
          [hoverUnlessShowingFocus]: {
            backgroundColor: alpha(tokens.primary.main, 0.06),
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

// Export design tokens for use in custom components
export { tokens, glass, shadows, easings, hoverUnlessShowingFocus };
export default theme;
